import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faPlus } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import {
  getAccountType,
  updateAccountType,
} from "../../api/Agreement";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import EditForm from "../../components/EditForm";
import Breadcrumbs from "../../components/Breadcrumbs";
import useAssignableLicenseOptions from "../../hooks/useAssignableLicenseOptions";

const EditAccountTypePage = () => {
  const { getAccessToken } = useAuth();
  const { agreementPublicId, accountTypePublicId } = useParams();
  const navigate = useNavigate();
  const licenseOptions = useAssignableLicenseOptions(agreementPublicId);
  const [accountType, setAccountType] = useState(null);
  const [loadingAccountType, setLoadingAccountType] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [accountTypes, setAccountTypes] = useState([]);

  useEffect(() => {
    if (accountTypePublicId) {
      handleGetAccountType();
    }
  }, [accountTypePublicId]);

  const handleGetAccountType = async () => {
    try {
      const accessToken = await getAccessToken();
      const newAccountType = await getAccountType(
        accountTypePublicId,
        accessToken
      );
      setAccountType(newAccountType);
    } catch (error) {
      console.error("Failed to fetch accountType:", error);
    } finally {
      setLoadingAccountType(false);
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: "Discard changes?",
        confirm: () => {
          navigate(-1);
        },
      }));
    } else {
      navigate(-1);
    }
  };

  const handleSaveAccountType = async () => {
    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        toast.error("Authorization failed. Please log in again.");
        return;
      }

      const payload = {
        agreementPublicId,
        accountTypePublicId: accountType.accountTypePublicId,
        name: accountType.name,
        price: accountType.price,
        minAmount: accountType.minAmount,
        status: accountType.status,
        createdBy: accountType.createdBy,
        comment: accountType.comment,
        // PBI #299: sent on every update, not only when it changed. The API maps a fresh
        // entity from the DTO and writes every column, so leaving this out nulls the
        // customer-facing text stored on the account type.
        customerDescription: accountType.customerDescription,
        licenseMpn: accountType.licenseMpn,
        updatedBy: accountType.updatedBy,
        updatedDateTimeUTC: accountType.updatedDateTimeUTC,
        isDefault: accountType.isDefault,
        // PBI #391: sent on every update for the same reason as customerDescription —
        // omitting it saves false and silently turns an on-prem account type into Entra.
        isOnPremAccount: accountType.isOnPremAccount,
      };

      if (accountTypePublicId) {
        // Update existing accountType
        await updateAccountType(
          payload,
          accessToken
        );
        toast.success("AccountType updated successfully", {
          iconTheme: {
            primary: "#4CAF50",
            secondary: "#DFF2BF",
          },
        });

      }
      navigate(-1); // Redirect to accountType list after creation

      setHasChanges(false);
    } catch (error) {
      toast.error("Failed to save accountType");
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage
              title={"AccountTypes"}
              loading={loadingAccountType}
            />
          </div>
          <div
            className={`${loadingAccountType ? "loading" : ""} page-content`}
          >
            <Breadcrumbs />

            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                Edit account type: {accountType?.name || "Unknown"}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={handleSaveAccountType}
                >Save
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <EditForm
                data={accountType || {}}
                setData={setAccountType}
                setHasChanges={setHasChanges}
                disabled={false}
                fields={[
                  {
                    key: "accountTypePublicId",
                    label: "Account type ID",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "name",
                    label: "Name",
                    type: "text",
                    disabled: false,
                  },
                  {
                    key: "price",
                    label: "Price",
                    type: "number",
                    disabled: false,
                  },
                  {
                    key: "minAmount",
                    label: "Minimum amount",
                    type: "number",
                    disabled: false,
                  },
                  {
                    key: "status",
                    label: "Status",
                    type: "dropdown",
                    options: [
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ],
                  },
                  {
                    key: "comment",
                    label: "Comment (internal)",
                    type: "text",
                    disabled: false,
                  },
                  {
                    // PBI #299: shown to the customer under the account type name in the
                    // customer portal. Capped to the server's [StringLength(1000)].
                    key: "customerDescription",
                    label: "Description (shown to the customer)",
                    type: "text-area",
                    maxLength: 1000,
                    disabled: false,
                  },
                  {
                    // PBI #318: the licence this account type implies. Empty means an
                    // account type change escalates to Diflex instead of swapping.
                    key: "licenseMpn",
                    label: "Licence part number (MPN)",
                    type: "suggest",
                    options: licenseOptions,
                    disabled: false,
                  },
                  {
                    key: "createdBy",
                    label: "Created by",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "createdDateTimeUTC",
                    label: "Created date",
                    type: "date",
                    disabled: true,
                  },
                  {
                    key: "updatedBy",
                    label: "Updated by",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "updatedDateTimeUTC",
                    label: "Updated date",
                    type: "date",
                    disabled: true,
                  },
                  {
                    key: "isDefault",
                    label: "Is default",
                    type: "switch",
                  },
                  {
                    key: "isOnPremAccount",
                    label: "Account lives in on-prem AD (Diflex creates it by hand, no Entra account is created)",
                    type: "switch",
                  },
                ]}
              />
            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title ? confirmState.title : "Discard changes?"}
          onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default EditAccountTypePage;
